import { LoadingSpinner } from "components/LoadingSpinner";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Sidebar } from "primereact/sidebar";
import { Tag } from "primereact/tag";
import styled from "styled-components";
import { IBenefitSelectionChange } from "types/BenefitSelection/BenefitSelection";

import { Surface } from "./Surface";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

const KIND_LABEL: Record<IBenefitSelectionChange["kind"], string> = {
  SAVE: "Saved",
  ENGINE_ON: "Engine turned on",
  ENGINE_OFF: "Engine turned off",
};

interface IHistoryDrawerProps {
  visible: boolean;
  onHide: () => void;
  changes: IBenefitSelectionChange[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * CLE-2792 — every saved change to this account's benefit configuration, newest first.
 *
 * 🚨 THE SUMMARY LINES COME FROM THE ENDPOINT AND ARE NOT COMPOSED HERE. It knows what each save
 * changed because it holds both versions; a client that diffed the current view against nothing
 * would be guessing, and a guess printed under "what changed" is the kind of thing a support call
 * ends up quoting back.
 *
 * ⚠️ AN EMPTY LIST IS A REAL ANSWER — nobody has saved anything on this account yet — and it reads
 * as one. Until review round 1 this drawer showed what was in force rather than what had changed,
 * with three paragraphs explaining which parts of a history it could not show; a reader wanting to
 * know who changed the service line last Tuesday got an essay about endpoints.
 */
export function HistoryDrawer({
  visible,
  onHide,
  changes,
  isLoading,
  isError,
}: IHistoryDrawerProps) {
  const whenCellBody = (change: IBenefitSelectionChange) => (
    <span className="text-sm">{new Date(change.at).toLocaleString()}</span>
  );

  const byCellBody = (change: IBenefitSelectionChange) => (
    <span className="text-sm">{change.byName ?? "Not a person"}</span>
  );

  const whatCellBody = (change: IBenefitSelectionChange) => (
    <div className="flex flex-column gap-1">
      <Tag
        value={KIND_LABEL[change.kind] ?? change.kind}
        className="surface-200 text-color align-self-start"
      />
      {change.summary.map((line) => (
        <span key={line} className="text-sm">
          {line}
        </span>
      ))}
    </div>
  );

  const noteCellBody = (change: IBenefitSelectionChange) => (
    <span className="text-sm text-color-secondary">{change.note ?? "—"}</span>
  );

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: "min(48rem, 100vw)" }}
      data-testid="benefitSelection.historyDrawer"
    >
      <Surface className="flex flex-column gap-3">
        <h3 className="m-0">History</h3>

        {isLoading && <LoadingSpinner />}

        {isError && (
          <span
            className="text-sm text-red-600"
            data-testid="benefitSelection.historyDrawer.error"
          >
            The history could not be loaded.
          </span>
        )}

        {!isLoading && !isError && changes.length === 0 && (
          <span
            className="text-sm text-color-secondary"
            data-testid="benefitSelection.historyDrawer.empty"
          >
            No changes have been saved yet.
          </span>
        )}

        {changes.length > 0 && (
          <StyledDataTable
            value={changes}
            dataKey="id"
            data-testid="benefitSelection.historyDrawer.table"
          >
            <Column
              header="When"
              body={whenCellBody}
              style={{ width: "12rem" }}
            />
            <Column header="Who" body={byCellBody} style={{ width: "10rem" }} />
            <Column header="What changed" body={whatCellBody} />
            <Column header="Note" body={noteCellBody} />
          </StyledDataTable>
        )}
      </Surface>
    </Sidebar>
  );
}
